import type { TimelineLayoutDTO } from '@tempo-io/roadmap-dto';
import { ItemTypeLevelApi } from '@/lib/api/common/itemTypeLevel';
import { TimelineLayoutApi } from '@/lib/api/loop-visualization/timeline-layout';
import { MissingItemTypeError } from './commonLayoutUtils';

/**
 * Creates a default timeline layout configuration for a visualization.
 */
export const getDefaultTimelineLayout = async (
  visualizationId: number,
): Promise<TimelineLayoutDTO> => {
  const itemTypeLevels = await ItemTypeLevelApi.getItemTypeLevels();
  const itemType = itemTypeLevels?.sort((level1, level2) => level1.level - level2.level)?.[0];

  if (!itemType) {
    throw new MissingItemTypeError(
      'Default timeline layout could not be created: missing item type level',
    );
  }

  return {
    visualizationType: 'timeline',
    visualizationId,
    itemLevelId: itemType.id,
    header1: 'status',
    header2: 'team',
    headerDirection: 'horizontal',
    theme: 'standard',
    showEmptyHeaders: false,
    colorConfig: null,
    labelByHeader1: null,
    labelByHeader2: null,
    showTodayLine: true,
    titleSize: 'medium',
    progressFieldType: 'custom',
    progressFieldId: null,
    showChildItems: true,
  };
};

/**
 * Validates a timeline layout and returns a valid configuration.
 */
export const getValidTimelineLayout = async (
  defaultLayout: TimelineLayoutDTO,
  layout: TimelineLayoutDTO | null,
): Promise<TimelineLayoutDTO> => {
  if (!layout) {
    return defaultLayout;
  }

  return {
    ...defaultLayout,
    ...layout,
  };
};

export const getAndValidateTimelineLayout = async (
  viewId: number,
  visualizationId: number,
): Promise<TimelineLayoutDTO> => {
  const [defaultLayoutResult, fetchedLayoutResult] = await Promise.allSettled([
    getDefaultTimelineLayout(visualizationId),
    TimelineLayoutApi.fetchTimelineLayout(viewId, visualizationId),
  ]);

  if (defaultLayoutResult.status === 'rejected') {
    throw defaultLayoutResult.reason;
  }

  const defaultLayout = defaultLayoutResult.value;
  const fetchedLayout =
    fetchedLayoutResult.status === 'fulfilled' ? fetchedLayoutResult.value : null;

  return getValidTimelineLayout(defaultLayout, fetchedLayout);
};
